import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import { URLParams, createUrl, request, stripTags } from 'app/utils';

import {
  useProducts,
  ProductsHeader,
  ProductsProgress,
  ProductsSidebarContinuation,
  ProductEvolutionDetailsApiResponse,
  ProductsContinuationApiResponse,
  // AvailableClassesApiResponse,
  ProductsCertificateDownload,
  ProductNextItems,
  ProductEnrollmentBox,
  AvailableClassesApiResponse,
} from 'features/products';
import { Box, Flex, Section, Typography, Loading, Page, SidebarButton, RichContent, SearchSelect, NotFound } from 'components';
import { ProductDetailsApiResponse } from 'features/products/types';
import { LivesApiResponse, useLives, LivesList } from 'features/lives';
import { TeachersList, TeachersApiResponse, useTeachers } from 'features/teachers';
import { useSchool } from 'features/school';
import { ModulesList, useModules, ModulesApiResponse } from 'features/modules';
import { useMenu } from 'features/menu';
import { AgreementsMessage } from 'features/agreements';
import { ContentsList } from 'features/contents';
import { ReportsList } from 'features/reports';
import { RecommendationsApiResponse, RecommendationsList, useRecommendations } from 'features/recommendations';
import { useTracking } from 'features/tracking';
import { ExamsList } from 'features/exams';
import { NextItemsArea } from 'app/types/cards/NextItemsArea';
import { ScormDetailsView } from 'features/scorm';
import { DownloadsBox } from 'features/downloads';
import { useResources } from 'features/resources';

interface ProductDetailsProps {
  productSlug: string;
  productClassUuid?: string;
  nextItems?: NextItemsArea;
  idDashboard?: boolean;
  enroll?: boolean;
}

const ProductDetailsView = ({ productSlug, productClassUuid, nextItems, idDashboard = false, enroll }: ProductDetailsProps): ReactElement => {
  const {
    productDetails,
    setProductDetails,
    clearProductDetails,
    productEvolutionDetails,
    setProductEvolutionDetails,
    clearProductEvolutionDetails,
    setProductsContinuation,
    clearProductsContinuation,
    availableClasses,
    setAvailableClasses,
    clearAvailableClasses,
  } = useProducts();
  const { lives, setLives, clearLives } = useLives();
  const { teachers, setTeachers, clearTeachers } = useTeachers();
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const { apiGatewayUrl, schoolMelver } = useSchool();
  const { modulesInfo, setModules, clearModules } = useModules();
  const { setRecommendations, clearRecommendations } = useRecommendations();
  const { backPathname, setBackPathname } = useMenu();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);

  const {
    usingTeachers,

    schools: {
      schoolMPC,
      schoolTestSchool,

      schoolBenndorf,
      schoolDZ,
      schoolXp,
    },
  } = useResources();

  // eslint-disable-next-line
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | false | null>(null);

  const getProductClassUuid = useCallback(
    (receivedProductClassUuid: string = '') => {
      return productClassUuid || receivedProductClassUuid || availableClasses?.current?.productClassUuid;
    },
    [availableClasses, productClassUuid]
  );

  const [searchParams] = useSearchParams();
  const { schoolAlias } = useSchool();
  const src = searchParams.get('src') || '';

  const apiFetch = useCallback(
    async (forceEnroll = false) => {
      if (forceEnroll) setLoading(true);

      setLoadingEdit(true);

      const params = URLParams({
        enroll: forceEnroll || enroll,
        sendEmail: (forceEnroll || enroll) && (schoolAlias !== 'b3'),
        src,
        playerCapabilities: '--empty',
      });

      await request({
        url: `/api/v1/School/Products/${productSlug}${getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}&${params}` : `?${params}`}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      })
        .then(async ({ data: productDetailsData }: AxiosResponse<ProductDetailsApiResponse>) => {
          setProductDetails(productDetailsData);

          if (forceEnroll || enroll) {
            window.location.href = getProductClassUuid()
              ? createUrl('productClassDetails', [productSlug, getProductClassUuid()])
              : createUrl('productDetails', [productSlug]);
          } else {
            await Promise.all([
              request({
                url: `/api/v1/School/ProductContentTimeline/${productSlug}${
                  getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
                }`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<ModulesApiResponse>) => {
                setModules(response.data);
              }),

              request({
                url: `/api/v1/School/ProductContentNextItems/${productSlug}${
                  getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
                }`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<ProductsContinuationApiResponse>) => {
                setProductsContinuation(response.data);
              }),

              productDetailsData?.enrolled
                ? request({
                    url: `/api/v1/School/ProductAssessments/${productSlug}${
                      getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
                    }`,
                    method: 'get',
                    baseURL: apiGatewayUrl,
                  }).then((response: AxiosResponse<ProductEvolutionDetailsApiResponse>) => {
                    setProductEvolutionDetails(response.data);
                  })
                : null,

              request({
                url: `/api/v1/School/ProductContentLives/${productSlug}${getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``}`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<LivesApiResponse>) => {
                setLives(response.data);
              }),

              request({
                url: `/api/v1/School/ProductContentTeachers/${productSlug}${
                  getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
                }`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<TeachersApiResponse>) => {
                setTeachers(response.data);
              }),

              request({
                url: `/api/v1/School/ProductContentRecommendations/${productSlug}${
                  getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
                }`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<RecommendationsApiResponse>) => {
                setRecommendations(response.data);
              }),

              request({
                url: `/api/v1/School/AvailableClasses/${productSlug}`,
                method: 'get',
                baseURL: apiGatewayUrl,
              }).then((response: AxiosResponse<AvailableClassesApiResponse>) => {
                setAvailableClasses(response.data);

                if (response.data?.current?.allowEnrollments) {
                  request({
                    url: `/api/v1/OfferPayment/Buy/${getProductClassUuid(response.data?.current?.productClassUuid)}`,
                    method: 'get',
                    baseURL: apiGatewayUrl,
                  })
                    .then(
                      (
                        response: AxiosResponse<{
                          link: string;
                        }>
                      ) => {
                        setPaymentLinkUrl(response.data.link);
                      }
                    )
                    .catch(() => {});
                }
              }),
            ])
              .catch(() => {})
              .finally(() => {
                setLoading(false);
                setLoadingEdit(false);
              });
          }
        })
        .catch(() => {
          setNotFound(true);
        });
    },
    [productSlug, getProductClassUuid(), src]
  );

  // const enrollStudent = useCallback(() => {
  //   setLoadingEdit(true);

  //   request({
  //     url: `/api/v1/OfferPayment/FreeEnroll/${getProductClassUuid()}`,
  //     method: 'get',
  //     baseURL: apiGatewayUrl,
  //   })
  //     .then(() => {
  //       window.location.reload();
  //     })
  //     .catch(() => {
  //       setLoadingEdit(false);
  //     });
  // }, [productSlug, getProductClassUuid()]);

  useEffect(() => {
    apiFetch();

    return () => {
      clearProductDetails();
      clearModules();
      clearLives();
      clearTeachers();
      clearProductEvolutionDetails();
      clearProductsContinuation();
      clearAvailableClasses();
      clearRecommendations();

      if (!backPathname) {
        setBackPathname(
          productClassUuid ? createUrl('productClassDetails', [productSlug, productClassUuid]) : createUrl('productDetails', [productSlug])
        );
      }
    };
  }, [productSlug, productClassUuid, src]);

  // const enroll = async (enrollUrl: string) => {
  //   await request({
  //     url: enrollUrl,
  //     method: 'get',
  //     baseURL: apiGatewayUrl,
  //   }).then(() => {
  //     window.location.reload();
  //   });
  // };

  // const isG10 = schoolUuid === '14d970bd-3794-4268-bf67-2c5d2d0f0285';

  // const isDom = schoolUuid === '3549e19f-b07f-4658-950d-694b99c6749d';

  const { eventTracker } = useTracking();

  useEffect(() => {
    if (productDetails) {
      if (schoolMPC) {
        eventTracker({ category: 'checkout', action: 'Pageview', label: productDetails?.product?.name });
      }
    }
  }, [productDetails, enroll]);

  if (loading) return <Loading label="Por favor, aguarde." />;

  if (notFound) return <NotFound />;

  if (productDetails?.course?.isScorm) {
    return <ScormDetailsView paymentLink={paymentLinkUrl} apiFetch={apiFetch} />;
  }

  return productDetails ? (
    <Page title={productDetails.product.name}>
      <AgreementsMessage productSlug={productSlug} />

      {loadingEdit && <Loading alpha />}

      <Flex flexGrow={0} flexDirection="column">
        <ProductsHeader />

        {!!nextItems && <ProductNextItems nextItems={nextItems} />}

        <Flex flexGrow={0} bg="gray_300">
          <Section py={{ m: 40, t: 60 }}>
            <Flex flexWrap={{ m: 'wrap', d: 'nowrap' }}>
              <Flex mr={{ m: 0, d: 60 }} flexGrow={1}>
                <Flex flexGrow={0} flexDirection="column">
                  <RichContent>{productDetails.course?.longHeaderDescription}</RichContent>
                </Flex>
              </Flex>

              <Flex
                width="100%"
                maxWidth={{ t: 450 }}
                mt={idDashboard ? {} : { m: 30, t: 50, d: -140 }}
                zIndex={100}
                flexDirection="column"
                flexShrink={0}
              >
                <Flex maxWidth={{ m: 280, t: 'auto' }} minWidth={{ m: '100%' }} flexGrow={0} mx="auto" flexDirection="column">
                  {Boolean(productDetails.enrolled) ? (
                    <>
                      {!idDashboard && !!availableClasses?.availables?.length && availableClasses?.availables?.length > 1 && (
                        <Box mb={2}>
                          <SearchSelect
                            borderColor="white"
                            name="itensPerPage"
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                              if (event.target.value) {
                                navigate(`${createUrl('productClassDetails', [productSlug, event.target.value])}`);
                              }
                            }}
                            defaultValue={productClassUuid || availableClasses?.current?.productClassUuid}
                          >
                            {availableClasses.availables.map((availableClass) => (
                              <option key={`availableClass-${availableClass.productClassUuid}`} value={availableClass.productClassUuid}>
                                {availableClass.description}
                              </option>
                            ))}
                          </SearchSelect>
                        </Box>
                      )}

                      {availableClasses?.current?.realTimeAssessments && (
                        <>
                          <Flex
                            py={{ m: 20, t: 30, d: 40 }}
                            flexDirection="column"
                            bg="white"
                            textAlign="center"
                            borderTopLeftRadius={4}
                            borderTopRightRadius={4}
                          >
                            <Typography as="span" type="type5" lineHeight="1.3" color="dark_350" fontWeight="bold">
                              {productSlug === 'na-rota-do-mercado-financeiro' ? 'Sua evolução na mentoria' : 'Sua evolução no curso'}
                            </Typography>

                            <Box mt={3}>
                              <ProductsProgress percent={productEvolutionDetails?.completionPercentage} />
                            </Box>
                          </Flex>

                          <SidebarButton
                            to={createUrl('productClassEvolution', [productSlug, modulesInfo?.productClassUuid])}
                            label="Minha evolução"
                          />
                        </>
                      )}

                      {productEvolutionDetails?.certificateIsAvailable && Boolean(productEvolutionDetails?.certificateAddress) && (
                        <>
                          <Flex mt={3} flexDirection="column">
                            <ProductsCertificateDownload filePath={productEvolutionDetails?.certificateAddress} />
                          </Flex>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <ProductEnrollmentBox paymentLinkUrl={paymentLinkUrl} apiFetch={apiFetch} />
                    </>
                  )}

                  <Flex mt={3}>
                    <ProductsSidebarContinuation />
                  </Flex>

                  {!!productDetails?.downloads?.length && (
                    <Flex mt={3}>
                      <DownloadsBox downloads={productDetails?.downloads} />
                    </Flex>
                  )}

                  {/* <Link
                    to={createUrl('productClassCheckout', [
                      productSlug,
                      modulesInfo?.productClassUuid,
                    ])}
                  >
                    <Button>Pagamento</Button>
                  </Link> */}
                </Flex>
              </Flex>
            </Flex>
          </Section>
        </Flex>

        {!!stripTags(productDetails.course?.longDescription) && (
          <Flex py={{ m: 40, t: 60 }} bg={productDetails.course?.allowTimelines ? 'white' : 'gray_100'}>
            <Section>
              <RichContent>{productDetails.course?.longDescription}</RichContent>
            </Section>
          </Flex>
        )}

        {productDetails.course?.allowTimelines ? (
          <Flex flexGrow={1} flexDirection="column">
            <ModulesList productSlug={productSlug} modules={modulesInfo} />
          </Flex>
        ) : (
          <>
            <ContentsList productSlug={productSlug} />
          </>
        )}

        <RecommendationsList productClassUuid={getProductClassUuid()} />

        <ExamsList local="products" productSlug={productSlug} />

        {((schoolMelver && (productSlug === 'mercado-de-opcoes' || productSlug === 'o-poder-das-opcoes' || productSlug === 'intromercadodeopcoes')) ||
          (schoolBenndorf && productSlug === 'starter') ||
          (schoolDZ && productSlug === 'conselheiro') ||
          (schoolXp && (productSlug === 'RendaVariavel' || productSlug === 'PlayBooks')) ||
          (schoolTestSchool && productSlug === 'Direitoadmnistrativo') ||
          (schoolMPC && productSlug === 'analises')) && <ReportsList local="products" productSlug={productSlug} />}

        {Boolean(lives && lives.length) && (
          <Flex flexGrow={1} my={{ m: 40, t: 60 }}>
            <LivesList lives={lives} enrolled={productDetails?.enrolled} productClassUuid={getProductClassUuid()} />
          </Flex>
        )}

        {usingTeachers && (
          <Flex flexGrow={1} my={{ m: 40, t: 60 }}>
            <TeachersList teachers={teachers} />
          </Flex>
        )}

        {!!productDetails.course?.longTrailerDescription && (
          <Flex my={{ m: 40, t: 60 }}>
            <Section>
              <RichContent>{productDetails.course?.longTrailerDescription}</RichContent>
            </Section>
          </Flex>
        )}
      </Flex>
    </Page>
  ) : (
    <Loading label="Por favor, aguarde." />
  );
};
export default ProductDetailsView;
